<template>
  <div class="results-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-trophy"></i>
        Resultados das Loterias
        <p class="page-subtitle">Gerencie os resultados das loterias e modalidades</p>
              </h1>
    </div>

    <!-- Navegação por tabs -->
    <div class="tabs-container">
      <nav class="modern-tabs">
        <button 
          class="tab-button" 
          :class="{ active: selectedTab === 1 }" 
          @click="selectedTab = 1"
        >
          <i class="fa-solid fa-list"></i>
          LISTAGEM
        </button>
        <button 
          v-if="canCreateEditDelete"
          class="tab-button" 
          :class="{ active: selectedTab === 2 }" 
          @click="lotteryResults.id ? selectedTab = 2 : createLotteryResults()"
        >
          <i class="fa-solid fa-plus"></i>
          {{ lotteryResults.id ? 'EDITAR' : 'CADASTRO' }}
        </button>
        <button 
          v-else
          class="tab-button disabled" 
          :disabled="true"
          :title="'Você não tem permissão para criar/editar resultados'"
        >
          <i class="fa-solid fa-plus"></i>
          CADASTRO
        </button>
      </nav>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando...</span>
      </div>
    </div>

    <!-- Tab 1: Listagem -->
    <div v-show="selectedTab === 1 && !loading" class="tab-content">
      <!-- Filtros -->
      <div class="filters-card">
        <div class="card-header">
          <h3 class="card-title">
            <i class="fa-solid fa-filter"></i>
            Filtros de Consulta
          </h3>
        </div>
        
        <div class="filters-content">
          <div class="filters-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Início *
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="searchDateFrom"
                placeholder="Data de início"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data Fim *
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="searchDateTo"
                placeholder="Data de fim"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-dice"></i>
                Loteria
              </label>
              <select 
                class="form-input" 
                v-model="searchLotteryId"
              >
                <option value="">Todas as loterias</option>
                <option 
                  v-for="l in lotteries" 
                  :key="l.id" 
                  :value="l.id"
                >
                  {{ l.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="filters-actions">
            <button 
              type="button" 
              class="btn-primary" 
              @click="searchLotteryResults(1)"
            >
              <i class="fa-solid fa-search"></i>
              Pesquisar
            </button>
            <button 
              type="button" 
              class="btn-secondary" 
              @click="clearFilters()"
            >
              <i class="fa-solid fa-eraser"></i>
              Limpar Filtros
            </button>
          </div>
        </div>
      </div>

      <!-- Mensagem informativa para usuários com permissões limitadas -->
      <div v-if="!canCreateEditDelete || isAgente" class="permission-info">
        <div class="info-card">
          <i class="fa-solid fa-info-circle"></i>
          <div class="info-content">
            <span v-if="isAgente">
              <strong>Modo AGENTE:</strong> Você pode criar, editar e excluir resultados das loterias, mas não pode visualizar os números sorteados nem os bilhetes premiados por questões de segurança.
            </span>
            <span v-else-if="isBanca">
              <strong>Modo BANCA:</strong> Você pode apenas visualizar os resultados das loterias. Apenas ADMIN e AGENTE podem criar, editar e excluir resultados.
            </span>
            <span v-else-if="isGerente">
              <strong>Modo GERENTE:</strong> Você pode apenas visualizar os resultados das loterias. Apenas ADMIN e AGENTE podem criar, editar e excluir resultados.
            </span>
            <span v-else-if="isCobrador">
              <strong>Modo COBRADOR:</strong> Você pode apenas visualizar os resultados das loterias. Apenas ADMIN e AGENTE podem criar, editar e excluir resultados.
            </span>
          </div>
        </div>
      </div>



      <!-- Tabela de Resultados -->
      <div v-show="lotteryResultsList && lotteryResultsList.length > 0" class="results-card">
        <div class="table-header">
          <h3 class="card-title">
            <i class="fa-solid fa-table"></i>
            Resultados Encontrados
            <span class="table-count">({{ totalRows }} resultado(s) encontrado(s))</span>
          </h3>
          <!-- Controles de Paginação Superior -->
          <div v-if="totalPages > 1" class="pagination-info">
            <span>Página {{ pageIndex }} de {{ totalPages }}</span>
            <div class="pagination-controls">
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex - 1)"
                :disabled="pageIndex <= 1"
              >
                <i class="fa-solid fa-chevron-left"></i>
              </button>
              <button 
                class="btn-pagination" 
                @click="changePage(pageIndex + 1)"
                :disabled="pageIndex >= totalPages"
              >
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
          <button 
            v-if="canCreateEditDelete"
            type="button" 
            class="btn-primary" 
            @click="createLotteryResults()"
          >
            <i class="fa-solid fa-plus"></i>
            Adicionar Resultado
          </button>
        </div>
        
        <div class="table-container">
          <table class="modern-table">
            <thead>
              <tr>
                <th>Data</th>
                <th>Loteria</th>
                <th>Horário</th>
                <th>Status</th>
                <th v-if="!isAgente">Bilhetes Premiados</th>
                <th width="190">Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="r in lotteryResultsList" 
                :key="r.id"
                class="data-row"
              >
                <td>
                  <div class="date-cell">
                    <i class="fa-solid fa-calendar-day"></i>
                    <span>{{ setDateTimeDisplay(r.date) }}</span>
                  </div>
                </td>
                <td>
                  <div class="lottery-cell">
                    <i class="fa-solid fa-dice"></i>
                    <span>{{ r.lottery || 'Loteria não informada' }}</span>
                  </div>
                </td>
                <td>
                  <span class="detail-text">{{ r.lotteryDetail || 'Horário não informado' }}</span>
                </td>
                <td>
                  <span :class="['status-badge', getStatusClass(r.processStatus, r.isProcessing)]">
                    <i :class="getStatusIcon(r.processStatus, r.isProcessing)"></i>
                    {{ getStatusLabel(r.processStatus, r.isProcessing) }}
                  </span>
                </td>
                <td v-if="!isAgente">
                  <div class="winning-numbers-cell">
                    <span
                      v-if="r.winningTicketsCount > 0"
                      class="winning-badge badge-success"
                    >
                      <i class="fa-solid fa-trophy"></i>
                      {{ r.winningTicketsCount }}
                    </span>
                    <span
                      v-else
                      class="winning-badge badge-muted"
                    >
                      0
                    </span>
                  </div>
                </td>
                <td>
                  <div class="action-buttons">
                    <button 
                      v-if="!isAgente"
                      class="btn-action btn-numbers" 
                      @click="showLotteryNumbers(r.id)"
                      title="Ver Números Sorteados"
                    >
                      <i class="fa-solid fa-hashtag"></i>
                    </button>
                    <button 
                      v-if="!isAgente"
                      class="btn-action btn-prizes" 
                      @click="showWinningTickets(r.id)"
                      title="Ver Bilhetes Premiados"
                    >
                      <i class="fa-solid fa-trophy"></i>
                    </button>
                    <button
                      v-if="canProcessLotteryResult(r) || processingResultId === r.id"
                      class="btn-action btn-process"
                      @click="processLotteryResult(r.id)"
                      :disabled="processingResultId === r.id"
                      :title="processingResultId === r.id ? 'Enviando processamento...' : 'Processar Resultado'"
                    >
                      <i :class="processingResultId === r.id ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-arrows-rotate'"></i>
                    </button>
                    <button 
                      v-if="canCreateEditDelete"
                      class="btn-action btn-edit" 
                      @click="editLotteryResults(r.id)"
                      title="Editar"
                    >
                      <i class="fa-solid fa-pen-to-square"></i>
                    </button>
                    <button 
                      v-if="canCreateEditDelete"
                      class="btn-action btn-delete" 
                      @click="deleteLotteryResults(r.id)"
                      title="Excluir"
                    >
                      <i class="fa-solid fa-trash"></i>
                    </button>
                    <span v-else-if="!canCreateEditDelete" class="no-actions">
                      <i class="fa-solid fa-eye"></i>
                      Apenas visualização
                    </span>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Controles de Paginação Inferior (igual ViewSellerBets) -->
        <div v-if="totalPages > 1" class="pagination-footer">
          <div class="pagination-info">
            <span>Mostrando {{ startItem }}-{{ endItem }} de {{ totalRows }} resultados</span>
          </div>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex - 1)"
              :disabled="pageIndex <= 1"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <span class="page-info">{{ pageIndex }} / {{ totalPages }}</span>
            <button 
              class="btn-pagination" 
              @click="changePage(pageIndex + 1)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(totalPages)"
              :disabled="pageIndex >= totalPages"
            >
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Estado vazio -->
      <div v-show="!lotteryResultsList || lotteryResultsList.length === 0" class="empty-state">
        <div class="empty-state-content">
          <i class="fa-solid fa-trophy"></i>
          <h3>Nenhum resultado encontrado</h3>
          <p>Configure o período e clique em pesquisar para visualizar os resultados</p>
          <button 
            v-if="canCreateEditDelete"
            type="button" 
            class="btn-primary" 
            @click="createLotteryResults()"
          >
            <i class="fa-solid fa-plus"></i>
            Criar Primeiro Resultado
          </button>
        </div>
      </div>

      <!-- Seção de Busca Manual de Resultados Externos -->
      <div class="external-results-section">
        <div class="external-results-card">
          <div class="card-header">
            <h3 class="card-title">
              <i class="fa-solid fa-search"></i>
              Buscar Resultados Externos
            </h3>
            <p class="card-subtitle">
              PT-RIO (RJ), FEDERAL (BR) e LS-RS (RS) vêm do resultadoonline.net
            </p>
          </div>

          <!-- Filtros de Busca Externa -->
          <div class="external-filters">
            <div class="external-filters-inline">
              <div class="input-group">
                <label class="input-label">
                  <i class="fa-solid fa-calendar-days"></i>
                  Data
                </label>
                <input 
                  class="form-input" 
                  type="date" 
                  v-model="externalSearchDate"
                  placeholder="Data do resultado"
                />
              </div>

              <div class="input-group">
                <label class="input-label">
                  <i class="fa-solid fa-map-marker-alt"></i>
                  Estado
                </label>
                <select 
                  class="form-input" 
                  v-model="externalSearchState"
                >
                  <option value="">Selecione um estado</option>
                  <option value="SP">São Paulo (SP)</option>
                  <option value="RJ">Rio de Janeiro (RJ) — PT-RIO</option>
                  <option value="RS">Rio Grande do Sul (RS) — LS-RS</option>
                  <option value="MG">Minas Gerais (MG)</option>
                  <option value="BA">Bahia (BA)</option>
                  <option value="PB">Paraíba (PB)</option>
                  <option value="GO">Goiás (GO)</option>
                  <option value="DF">Distrito Federal (DF)</option>
                  <option value="BR">Brasil (BR) — FEDERAL</option>
                  <option value="PTSUL">PT-SUL</option>
                </select>
              </div>

              <div class="input-group">
                <button 
                  type="button" 
                  class="btn-primary btn-search-inline" 
                  @click="searchExternalResults()"
                  :disabled="loadingExternalResults || !externalSearchDate || !externalSearchState"
                >
                  <i class="fa-solid fa-search"></i>
                  Buscar Resultados
                </button>
              </div>
            </div>

            <div class="external-info">
              <i class="fa-solid fa-info-circle"></i>
              <span>* Todos os resultados seguem o horário de Brasília (GMT-3).</span>
            </div>
          </div>

          <!-- Loading dos resultados externos -->
          <div v-show="loadingExternalResults" class="external-loading">
            <div class="loading-spinner">
              <i class="fa-solid fa-spinner fa-spin"></i>
              <span>Buscando resultados...</span>
            </div>
          </div>

          <!-- Resultados Externos Encontrados -->
          <div v-show="externalResults && externalResults.length > 0 && !loadingExternalResults" class="external-results-list">
            <h4 class="external-results-title">
              <i class="fa-solid fa-list"></i>
              Resultados Encontrados ({{ externalResults.length }})
            </h4>

            <div class="external-results-grid">
              <div 
                v-for="(result, index) in externalResults" 
                :key="index"
                class="external-result-card"
              >
                <div class="external-result-header">
                  <h5 class="external-result-title">
                    <i class="fa-solid fa-trophy"></i>
                    {{ result.lotteryName }}
                  </h5>
                </div>

                <div class="external-result-numbers">
                  <table class="external-numbers-table">
                    <thead>
                      <tr>
                        <th>Prêmio</th>
                        <th>Número</th>
                        <th>Grupo</th>
                        <th>Animal</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(number, numIndex) in result.numbers" :key="numIndex">
                        <td>
                          <span class="prize-badge">{{ numIndex + 1 }}º</span>
                        </td>
                        <td>
                          <span class="number-display">{{ formatExternalNumber(number, numIndex + 1) }}</span>
                        </td>
                        <td>
                          <span class="group-badge">
                            {{ getGroupDisplay(result, numIndex, number) }}
                          </span>
                        </td>
                        <td>
                          <span class="animal-text">
                            {{ getAnimalDisplay(result, numIndex, number) }}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="canCreateEditDelete" class="external-result-actions">
                  <button 
                    type="button" 
                    class="btn-import" 
                    @click="importExternalResult(result)"
                    :disabled="importingResultId === result.lotteryName"
                  >
                    <i class="fa-solid fa-download"></i>
                    {{ importingResultId === result.lotteryName ? 'Importando...' : 'Importar para Sistema' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mensagem quando não há resultados externos -->
          <div v-show="externalResultsSearched && (!externalResults || externalResults.length === 0) && !loadingExternalResults" class="external-empty-state">
            <i class="fa-solid fa-search"></i>
            <p>Nenhum resultado encontrado para a data e estado selecionados.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Tab 2: Formulário -->
    <div v-show="selectedTab === 2" class="tab-content">
      <div class="form-card">
        <div class="form-header">
          <h3 class="form-title">
            <i class="fa-solid fa-trophy"></i>
            {{ lotteryResults.id ? 'Editar Resultado' : 'Novo Resultado' }}
            <p class="form-subtitle">
              {{ lotteryResults.id ? 'Atualize as informações do resultado' : 'Preencha as informações do novo resultado' }}
            </p>
          </h3>
        </div>

        <div class="form-body">
          <div class="form-grid">
            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-dice"></i>
                Loteria *
              </label>
              <select 
                class="form-input" 
                v-model="lotteryResults.lotteryId" 
                @change="onLotteryChange()"
              >
                <option value="">Selecione uma loteria</option>
                <option 
                  v-for="l in lotteries" 
                  :key="l.id" 
                  :value="l.id"
                  :selected="lotteryResults.lotteryId == l.id"
                >
                  {{ l.name }}
                </option>
              </select>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-info-circle"></i>
                Horário *
              </label>
              <select 
                class="form-input" 
                v-model="lotteryResults.lotteryDetailId"
                :disabled="lotteryResults.lotteryId == null"
              >
                <option value="">Selecione um Horário</option>
                <option 
                  v-for="d in lotteryDetails" 
                  :key="d.id" 
                  :value="d.id"
                  :selected="lotteryResults.lotteryDetailId == d.id"
                >
                  {{ d.description }}
                </option>
              </select>
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-calendar-days"></i>
                Data *
              </label>
              <input 
                class="form-input" 
                type="date" 
                v-model="lotteryResults.date"
              />
            </div>

            <div class="input-group">
              <label class="input-label">
                <i class="fa-solid fa-cog"></i>
                Status
              </label>
              <div class="status-display">
                <span :class="['status-badge', getStatusClass(lotteryResults.processStatus, lotteryResults.isProcessing)]">
                  <i :class="getStatusIcon(lotteryResults.processStatus, lotteryResults.isProcessing)"></i>
                  {{ getStatusLabel(lotteryResults.processStatus, lotteryResults.isProcessing) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Tabela de Números -->
          <div class="numbers-section">
            <h4 class="section-title">
              <i class="fa-solid fa-hashtag"></i>
              Números Sorteados
            </h4>
            
            <div class="numbers-table-container">
              <table class="numbers-table">
                <thead>
                  <tr>
                    <th>{{ isNumericLotteryForm ? 'Dezena' : 'Posição' }}</th>
                    <th>Número</th>
                    <th v-if="!isNumericLotteryForm">Grupo</th>
                    <th v-if="!isNumericLotteryForm">Animal</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="n in lotteryResults.lotteryResultsNumbers" :key="n.id">
                    <td>
                      <div class="position-cell">
                        <i class="fa-solid fa-award"></i>
                        <span>{{ isNumericLotteryForm ? `Dezena ${n.place}` : `${n.place}º Lugar` }}</span>
                      </div>
                    </td>
                    <td>
                      <input 
                        type="text" 
                        :maxlength="isNumericLotteryForm ? 2 : (n.place === 7 ? 3 : 4)"
                        class="number-input"
                        v-model="n.number"
                        @input="onNumberInput(n)"
                        :placeholder="isNumericLotteryForm ? '00' : (n.place === 7 ? '000' : '0000')"
                      />
                    </td>
                    <td v-if="!isNumericLotteryForm">
                      <input
                        type="text"
                        maxlength="2"
                        class="number-input group-input"
                        v-model="n.group"
                        placeholder="Ex: 21"
                        title="Número do grupo (1-25)"
                      />
                    </td>
                    <td v-if="!isNumericLotteryForm">
                      <input
                        type="text"
                        maxlength="32"
                        class="number-input animal-input"
                        v-model="n.animal"
                        placeholder="Ex: Touro"
                        title="Nome do animal"
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p class="form-hint">
              <i class="fa-solid fa-info-circle"></i>
              <template v-if="isNumericLotteryForm">
                Informe {{ numericLotteryConfig.count }} dezenas diferentes entre
                01 e {{ String(numericLotteryConfig.poolMax).padStart(2, '0') }}.
              </template>
              <template v-else>
                Grupo e Animal são opcionais. Do 1º ao 6º prêmio informe 4 dígitos; o 7º prêmio é uma centena de 3 dígitos.
              </template>
            </p>
          </div>
        </div>

        <div class="form-footer">
          <button 
            type="button" 
            class="btn-primary" 
            @click="lotteryResults.id ? updateLotteryResults() : insertLotteryResults()"
            :disabled="saving"
          >
            <i :class="saving ? 'fa-solid fa-spinner fa-spin' : 'fa-solid fa-save'"></i>
            {{ saving ? 'Salvando...' : (lotteryResults.id ? 'Atualizar' : 'Salvar') }}
          </button>
          <button 
            type="button" 
            class="btn-secondary" 
            @click="selectedTab = 1"
            :disabled="saving"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Bilhetes Premiados -->
    <WinningTicketsModal 
      :visible="showWinningTicketsModal"
      :lottery-result-id="selectedLotteryResultId"
      @close="closeWinningTicketsModal"
    />

    <!-- Modal de Números Sorteados -->
    <div v-if="showLotteryNumbersModal" class="modal-overlay" @click="closeLotteryNumbersModal">
      <div class="modal-content numbers-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            <i class="fa-solid fa-hashtag"></i>
            Números Sorteados
          </h3>
          <button class="modal-close" @click="closeLotteryNumbersModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>

        <div v-if="loadingNumbers" class="modal-loading">
          <div class="loading-spinner">
            <i class="fa-solid fa-spinner fa-spin"></i>
            <span>Carregando números...</span>
          </div>
        </div>

        <div v-else-if="selectedLotteryNumbers" class="modal-body">
          <div class="lottery-info">
            <div class="info-item">
              <i class="fa-solid fa-dice"></i>
              <span class="info-label">Loteria:</span>
              <span class="info-value">{{ selectedLotteryNumbers.lottery || 'Loteria não informada' }}</span>
            </div>
            <div class="info-item">
              <i class="fa-solid fa-clock"></i>
              <span class="info-label">Horário:</span>
              <span class="info-value">{{ selectedLotteryNumbers.lotteryDetail || 'Horário não informado' }}</span>
            </div>
            <div class="info-item">
              <i class="fa-solid fa-calendar-day"></i>
              <span class="info-label">Data:</span>
              <span class="info-value">{{ setDateTimeDisplay(selectedLotteryNumbers.date) }}</span>
            </div>
          </div>

          <div class="numbers-section">
            <h4 class="section-title">
              <i class="fa-solid fa-award"></i>
              Números Premiados
            </h4>
            
            <div class="numbers-grid">
              <div 
                v-for="number in selectedLotteryNumbers.lotteryResultsNumbers" 
                :key="number.id"
                class="number-card"
              >
                <div v-if="!isNumericLotteryResult(selectedLotteryNumbers)" class="number-position">{{ number.place }}º Lugar</div>
                <div class="number-value">{{ formatLotteryResultNumber(number, selectedLotteryNumbers) }}</div>
                <div v-if="!isNumericLotteryResult(selectedLotteryNumbers)" class="number-meta">
                  <span class="number-group">
                    Grupo {{ getModalNumberGroup(number) }}
                  </span>
                  <span class="number-animal">{{ getModalNumberAnimal(number) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button class="btn-secondary" @click="closeLotteryNumbersModal">
            <i class="fa-solid fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import { ROLES } from "@/services/permissions";
import WinningTicketsModal from "@/components/WinningTicketsModal.vue";

export default {
  components: {
    WinningTicketsModal
  },
  data: () => ({
    quickFilter: "",
    selectedTab: 1,
    lotteries: [],
    lotteryDetails: [],
    lotteryResultsList: [],
    lotteryResults: {},
    loading: true,
    saving: false,
    searchDateFrom: "0000-00-00",
    searchDateTo: "0000-00-00",
    searchLotteryId: null, // Filtro por loteria
    pages: [],
    pageIndex: 1,
    totalRows: 0,
    
    // Controle de usuário e permissões
    currentUser: null,
    
    // Controle de região
    currentRegion: null,

    // Modal de bilhetes premiados
    showWinningTicketsModal: false,
    selectedLotteryResultId: null,

    // Modal de números sorteados
    showLotteryNumbersModal: false,
    selectedLotteryNumbers: null,
    loadingNumbers: false,

    // Busca de resultados externos
    externalSearchDate: "",
    externalSearchState: "",
    externalResults: [],
    loadingExternalResults: false,
    externalResultsSearched: false,
    importingResultId: null,
    processingResultId: null,
  }),
  computed: {
    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    isAgente() {
      return this.currentUser && this.currentUser.role === ROLES.AGENTE;
    },

    canCreateEditDelete() {
      // ADMIN e AGENTE podem criar/editar/excluir resultados
      // BANCA, GERENTE e COBRADOR apenas visualizam
      return this.isAdmin || this.isAgente;
    },

    selectedFormLotteryName() {
      const selected = this.lotteries.find(
        lottery => lottery.id === this.lotteryResults.lotteryId
      );
      return String(selected?.name || this.lotteryResults.lottery || "")
        .trim()
        .toUpperCase();
    },

    numericLotteryConfig() {
      const configs = {
        LOTINHA: { count: 15, poolMax: 25 },
        QUININHA: { count: 5, poolMax: 80 },
        SENINHA: { count: 6, poolMax: 60 }
      };
      return configs[this.selectedFormLotteryName] || null;
    },

    isNumericLotteryForm() {
      return this.numericLotteryConfig !== null;
    },

    totalPages() {
      return this.pages.length || 0;
    },
    startItem() {
      return this.totalRows === 0 ? 0 : (this.pageIndex - 1) * 10 + 1;
    },
    endItem() {
      if (this.totalRows === 0) return 0;
      return Math.min(this.pageIndex * 10, this.totalRows);
    }
  },
  async mounted() {
    this.loading = true;

    try {
      await this.loadCurrentUser();
      await this.loadCurrentRegion();
      
      // Inicializar as datas
      const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      let dateTo = new Date();
      let dateFrom = new Date();
      dateFrom.setMonth(dateFrom.getMonth() - 1);
      this.searchDateFrom = _fmt(dateFrom);
      this.searchDateTo = _fmt(dateTo);

      // Inicializar data de busca externa com data atual
      this.externalSearchDate = _fmt(dateTo);

      // Carregar loterias primeiro para enriquecer os dados
      await this.loadLotteries();
      await this.searchLotteryResults(1);
    } catch (error) {
      this.loading = false;
      toast.error(handleErrorMessage(error));
    }
    
    // Adicionar listener para mudanças de região
    window.addEventListener('regionChanged', this.onRegionChanged);
  },
  beforeUnmount() {
    // Remover listener para mudanças de região
    window.removeEventListener('regionChanged', this.onRegionChanged);
  },
  methods: {
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
        }
      } catch (error) {
        this.currentUser = null;
        toast.error("Erro ao carregar dados do usuário");
      }
    },
    async loadCurrentRegion() {
      try {
        const regionData = localStorage.getItem('currentRegion');
        if (regionData) {
          this.currentRegion = JSON.parse(regionData);
        }
      } catch (error) {
        this.currentRegion = null;
        toast.error("Erro ao carregar dados da região");
      }
    },
    onRegionChanged(event) {
      // Recarregar região do localStorage
      this.loadCurrentRegion();
      // Recarregar os resultados com a nova região
      this.searchLotteryResults(1);
    },
    validateLotteryResults() {
      if (this.lotteryResults.lotteryDetailId == null ||
        this.lotteryResults.date == null) {
        toast.error("Por favor, preencha todos os campos obrigatórios!");
        return false;
      }

      const numbers = this.lotteryResults.lotteryResultsNumbers || [];
      if (this.isNumericLotteryForm) {
        const { count, poolMax } = this.numericLotteryConfig;
        if (numbers.length !== count || numbers.some(number => !number.number)) {
          toast.error(`Informe as ${count} dezenas sorteadas!`);
          return false;
        }

        const parsed = numbers.map(number => Number(number.number));
        if (parsed.some(number => !Number.isInteger(number) || number < 1 || number > poolMax)) {
          toast.error(`As dezenas devem estar entre 01 e ${String(poolMax).padStart(2, '0')}!`);
          return false;
        }
        if (new Set(parsed).size !== parsed.length) {
          toast.error("Não é permitido informar dezenas repetidas!");
          return false;
        }
      } else if (numbers.length !== 7 || numbers.some(number => !number.number)) {
        toast.error("Informe os 7 números do resultado!");
        return false;
      }

      return true;
    },
    buildResultNumbers(count = 7) {
      return Array.from({ length: count }, (_, index) => ({
        id: null,
        place: index + 1,
        number: "",
        group: "",
        animal: ""
      }));
    },
    createLotteryResults() {
      if (!this.canCreateEditDelete) {
        toast.error("Você não tem permissão para criar resultados!");
        return;
      }

      this.lotteryResults = {
        id: null,
        lotteryId: null,
        lotteryDetailId: null,
        date: this.searchDateTo,
        endOfProcessingDate: null,
        isProcessing: null,
        processStatus: null,
        lotteryResultsNumbers: this.buildResultNumbers()
      }
      this.selectedTab = 2;
      this.searchLotteries();
    },
    editLotteryResults(id) {
      if (!this.canCreateEditDelete) {
        toast.error("Você não tem permissão para editar resultados!");
        return;
      }

      this.selectedTab = 2;
      this.getById(id);
    },
    async loadLotteries() {
      try {
        const response = await mainService.searchLotteries();
        this.lotteries = response.data;
      } catch (error) {
        toast.error(handleErrorMessage(error));
      }
    },
    searchLotteries() {
      mainService
        .searchLotteries()
        .then((response) => {
          this.lotteries = response.data;
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
    },
    onLotteryChange() {
      // Limpar o horário selecionado quando a loteria muda
      this.lotteryResults.lotteryDetailId = null;
      const count = this.numericLotteryConfig?.count || 7;
      this.lotteryResults.lotteryResultsNumbers = this.buildResultNumbers(count);
      // Carregar os novos detalhes da loteria
      this.searchLotteryDetails();
    },
    searchLotteryDetails() {
      if (!this.lotteryResults.lotteryId) {
        this.lotteryDetails = [];
        return;
      }

      // Primeiro, tentar encontrar a loteria nos dados já carregados
      const selectedLottery = this.lotteries.find(lottery => 
        lottery.id == this.lotteryResults.lotteryId
      );
      
      if (selectedLottery && selectedLottery.details) {
        this.lotteryDetails = selectedLottery.details;
        return;
      }

      // Se não encontrou ou não tem detalhes, fazer nova consulta
      mainService
        .searchLotteries()
        .then((response) => {
          // Atualizar a lista de loterias com os novos dados
          this.lotteries = response.data;
          
          // Encontrar a loteria selecionada e extrair seus detalhes
          const freshSelectedLottery = response.data.find(lottery => 
            lottery.id == this.lotteryResults.lotteryId
          );
          
          if (freshSelectedLottery && freshSelectedLottery.details) {
            this.lotteryDetails = freshSelectedLottery.details;
          } else {
            this.lotteryDetails = [];
          }
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
          this.lotteryDetails = [];
        })
    },
    async searchLotteryResults(pageIndex, ignoreRenderPages) {
      this.pageIndex = pageIndex;
      this.loading = true;
      
      // Determinar se deve filtrar por região baseado nas permissões
      let regionIdToFilter = null;
      
      if (this.isAdmin || this.isAgente) {
        // ADMIN e AGENTE: apenas filtram por região se uma região específica estiver selecionada
        // Se nenhuma região estiver selecionada, mostram todos os resultados
        if (this.currentRegion && this.currentRegion.id) {
          regionIdToFilter = this.currentRegion.id;
        }
      } else {
        // BANCA, GERENTE, COBRADOR: sempre filtram pela região selecionada
        // Se não há região selecionada, não mostram resultados
        if (this.currentRegion && this.currentRegion.id) {
          regionIdToFilter = this.currentRegion.id;
        } else {
          // Para usuários não-ADMIN e não-AGENTE sem região selecionada, não buscar resultados
          this.lotteryResultsList = [];
          this.loading = false;
          return;
        }
      }
      
      // Determinar filtro de loteria
      let lotteryIdToFilter = this.searchLotteryId || null;
      
      try {
        const response = await mainService.searchLotteryResults(
          this.searchDateFrom,
          this.searchDateTo,
          pageIndex,
          regionIdToFilter,
          lotteryIdToFilter
        );
        let results = response.data.result;
        
        // Enriquecer resultados com informações de loteria se necessário
        if (this.lotteries && this.lotteries.length > 0) {
          results = results.map(result => {
            if (!result.lottery || !result.lotteryDetail) {
              const lottery = this.lotteries.find(l => l.id === result.lotteryId);
              if (lottery) {
                if (!result.lottery) {
                  result.lottery = lottery.name;
                }
                if (!result.lotteryDetail && lottery.details) {
                  const detail = lottery.details.find(d => d.id === result.lotteryDetailId);
                  if (detail) {
                    result.lotteryDetail = detail.description;
                  }
                }
              }
            }
            return result;
          });
        }

        // Ordenar resultados por data (mais recentes primeiro)
        this.lotteryResultsList = results.sort((a, b) => {
          const dateA = new Date(a.date);
          const dateB = new Date(b.date);
          return dateB - dateA; // Ordem decrescente (mais recentes primeiro)
        });

        this.totalRows = response.data.totalRows || 0;
        if (!ignoreRenderPages) {
          this.pages = [];
          let pages = Math.ceil((response.data.totalRows || 0) / 10);
          for (let i = 1; i <= pages; i++)
            this.pages.push(i);
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.loading = false;
      }
    },
    changePage(newPage) {
      if (newPage >= 1 && newPage <= this.totalPages) {
        this.searchLotteryResults(newPage, true);
      }
    },
    getById(id) {
      this.loading = true;
      mainService
        .getLotteryResultsById(id)
        .then((response) => {
          this.lotteryResults = response.data;
          this.lotteryResults.date = this.setDateInput(this.lotteryResults.date)
          this.searchLotteries();
          this.searchLotteryDetails();
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async insertLotteryResults() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditDelete) {
          toast.error("Você não tem permissão para criar resultados!");
          return;
        }

        if (!this.validateLotteryResults()) {
          return;
        }

        this.lotteryResults.lotteryResultsNumbers.forEach(c => {
          c.number = c.number.toString();
        });

        await mainService.insertLotteryResults(this.lotteryResults);
        toast.success("Resultado cadastrado com sucesso!");
        this.lotteryResults = {};
        this.searchLotteryResults(1);
        this.selectedTab = 1;
      } catch (error) {
        const errorMessage = handleErrorMessage(error);
        const lower = errorMessage.toLowerCase();
        if (
          lower.includes("já existe") ||
          lower.includes("duplicado") ||
          lower.includes("already exists")
        ) {
          toast.warning(errorMessage);
        } else {
          toast.error(errorMessage);
        }
      } finally {
        this.saving = false;
      }
    },
    async updateLotteryResults() {
      if (this.saving) return;
      this.saving = true;

      try {
        if (!this.canCreateEditDelete) {
          toast.error("Você não tem permissão para atualizar resultados!");
          return;
        }

        if (!this.validateLotteryResults()) {
          return;
        }

        this.lotteryResults.lotteryResultsNumbers.forEach(c => {
          c.number = c.number.toString();
        });

        await mainService.updateLotteryResults(this.lotteryResults);
        toast.success("Resultado atualizado com sucesso!");
        this.lotteryResults = {};
        this.searchLotteryResults(1);
        this.selectedTab = 1;
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    },
    deleteLotteryResults(id) {
      if (!this.canCreateEditDelete) {
        toast.error("Você não tem permissão para excluir resultados!");
        return;
      }

      this.$confirm({
        message: "Confirma a exclusão deste resultado?",
        button: { no: "Não", yes: "Sim" },
        callback: (confirm) => {
          if (confirm) {
            this.loading = true;
            mainService
              .deleteLotteryResults(id)
              .then(() => {
                toast.success("Resultado excluído com sucesso!");
                this.searchLotteryResults(1);
              })
              .catch((error) => {
                toast.error(handleErrorMessage(error));
              })
              .finally(() => {
                this.loading = false;
              });
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0].focus();
      }, 1);
    },
    setDateDisplay(value) {
      return jsFunctions.setDateDisplay(value);
    },
    setDateTimeDisplay(value) {
      return jsFunctions.setDateTimeDisplay(value);
    },
    setDateInput(value) {
      return jsFunctions.setDateInput(value);
    },
    onlyNumbers(event) {
      event.target.value = event.target.value.replace(/\D/g, '');
    },
    onNumberInput(n) {
      const digits = String(n.number).replace(/\D/g, '');
      if (this.isNumericLotteryForm) {
        n.number = digits.slice(0, 2);
        n.group = "";
        n.animal = "";
        return;
      }
      // O 7º prêmio é sempre uma centena. Se vier um milhar, usar os 3 últimos dígitos.
      n.number = n.place === 7 ? digits.slice(-3) : digits.slice(0, 4);
      const expectedLength = n.place === 7 ? 3 : 4;

      if (n.number.length === expectedLength) {
        const derived = this.deriveGrupoAnimal(n.number.padStart(4, '0'));
        if (derived) {
          n.group = String(derived.grupo).padStart(2, '0');
          n.animal = derived.animal;
        }
      }
    },
    // Retorna grupo para exibição no modal de números sorteados
    getModalNumberGroup(number) {
      if (number.group) return String(number.group).padStart(2, '0');
      const derived = this.deriveGrupoAnimal(String(number.number || '').padStart(4, '0'));
      return derived ? String(derived.grupo).padStart(2, '0') : '—';
    },
    // Retorna animal para exibição no modal de números sorteados
    getModalNumberAnimal(number) {
      if (number.animal) return number.animal;
      const derived = this.deriveGrupoAnimal(String(number.number || '').padStart(4, '0'));
      return derived ? derived.animal : '—';
    },
    isNumericLotteryResult(result) {
      return ['LOTINHA', 'QUININHA', 'SENINHA'].includes(
        String(result?.lottery || '').trim().toUpperCase()
      );
    },
    formatLotteryResultNumber(number, result) {
      if (this.isNumericLotteryResult(result)) {
        return String(number?.number || '').replace(/\D/g, '').padStart(2, '0');
      }
      return this.formatNumberByPosition(number?.number, number?.place);
    },
    // Retorna o grupo para exibição na tabela de busca manual — usa dado da API ou deriva pelo helper
    getGroupDisplay(result, numIndex, number) {
      const fromApi = result.groups && result.groups[numIndex];
      if (fromApi) return fromApi;
      const derived = this.deriveGrupoAnimal(String(number || '').padStart(4, '0'));
      return derived ? String(derived.grupo).padStart(2, '0') : '—';
    },
    // Retorna o animal para exibição na tabela de busca manual — usa dado da API ou deriva pelo helper
    getAnimalDisplay(result, numIndex, number) {
      const fromApi = result.animals && result.animals[numIndex];
      if (fromApi) return fromApi;
      const derived = this.deriveGrupoAnimal(String(number || '').padStart(4, '0'));
      return derived ? derived.animal : '—';
    },
    // Tabela oficial do Jogo do Bicho (espelha JogoDoBichoHelper.cs)
    deriveGrupoAnimal(milhar) {
      const ANIMAIS = [
        '',          // 0 (não usado)
        'Avestruz',  // 01 — dezenas 01-04
        'Águia',     // 02 — dezenas 05-08
        'Burro',     // 03 — dezenas 09-12
        'Borboleta', // 04 — dezenas 13-16
        'Cachorro',  // 05 — dezenas 17-20
        'Cabra',     // 06 — dezenas 21-24
        'Carneiro',  // 07 — dezenas 25-28
        'Camelo',    // 08 — dezenas 29-32
        'Cobra',     // 09 — dezenas 33-36
        'Coelho',    // 10 — dezenas 37-40
        'Cavalo',    // 11 — dezenas 41-44
        'Elefante',  // 12 — dezenas 45-48
        'Galo',      // 13 — dezenas 49-52
        'Gato',      // 14 — dezenas 53-56
        'Jacaré',    // 15 — dezenas 57-60
        'Leão',      // 16 — dezenas 61-64
        'Macaco',    // 17 — dezenas 65-68
        'Porco',     // 18 — dezenas 69-72
        'Pavão',     // 19 — dezenas 73-76
        'Peru',      // 20 — dezenas 77-80
        'Touro',     // 21 — dezenas 81-84
        'Tigre',     // 22 — dezenas 85-88
        'Urso',      // 23 — dezenas 89-92
        'Veado',     // 24 — dezenas 93-96
        'Vaca',      // 25 — dezenas 97-00
      ];
      if (!milhar || milhar.length < 4) return null;
      const dezena = parseInt(milhar.slice(-2), 10);
      if (isNaN(dezena)) return null;
      // Dezena 00 → grupo 25 (Vaca)
      const grupo = dezena === 0 ? 25 : Math.ceil(dezena / 4);
      if (grupo < 1 || grupo > 25) return null;
      return { grupo, animal: ANIMAIS[grupo] };
    },
    normalizeStatus(status) {
      return String(status || "").trim().toLowerCase();
    },
    isUnprocessedStatus(status) {
      const statusLower = this.normalizeStatus(status);
      return !statusLower ||
        statusLower.includes('não processado') ||
        statusLower.includes('nao processado');
    },
    isProcessedStatus(status) {
      const statusLower = this.normalizeStatus(status);
      if (!statusLower || this.isUnprocessedStatus(status)) {
        return false;
      }

      return statusLower.includes('processado') ||
        statusLower.includes('concluído') ||
        statusLower.includes('concluido') ||
        statusLower.includes('finalizado') ||
        statusLower.includes('completo');
    },
    getStatusLabel(status, isProcessing = false) {
      if (isProcessing) return 'Processando';
      if (this.isUnprocessedStatus(status)) return 'Não processado';
      return status;
    },
    getStatusClass(status, isProcessing = false) {
      if (isProcessing) return 'badge-warning';
      if (this.isUnprocessedStatus(status)) return 'badge-danger';

      const statusLower = this.normalizeStatus(status);

      if (this.isProcessedStatus(status)) {
        return 'badge-success';
      }

      if (statusLower.includes('erro') ||
          statusLower.includes('falhou') ||
          statusLower.includes('falha') ||
          statusLower.includes('rejeitado')) {
        return 'badge-danger';
      }

      if (statusLower.includes('pendente') ||
          statusLower.includes('aguardando') ||
          statusLower.includes('processando')) {
        return 'badge-warning';
      }

      return 'badge-danger';
    },
    getStatusIcon(status, isProcessing = false) {
      if (isProcessing) return 'fa-solid fa-spinner fa-spin';
      if (this.isUnprocessedStatus(status)) return 'fa-solid fa-clock';
      if (this.isProcessedStatus(status)) {
        return 'fa-solid fa-check-circle';
      }
      if (this.normalizeStatus(status).includes('erro') || this.normalizeStatus(status).includes('falhou')) {
        return 'fa-solid fa-exclamation-circle';
      }
      return 'fa-solid fa-clock';
    },
    canProcessLotteryResult(result) {
      if (!this.canCreateEditDelete || !result || result.isProcessing) {
        return false;
      }

      return this.isUnprocessedStatus(result.processStatus);
    },
    processLotteryResult(id) {
      if (!this.canCreateEditDelete) {
        toast.error("Você não tem permissão para processar resultados!");
        return;
      }

      const result = this.lotteryResultsList.find(item => item.id === id);

      if (!this.canProcessLotteryResult(result)) {
        toast.info("Este resultado já foi processado ou está em processamento.");
        return;
      }

      this.$confirm({
        message: "Deseja iniciar o processamento deste resultado?",
        button: { no: "Não", yes: "Sim" },
        callback: async (confirm) => {
          if (confirm) {
            this.processingResultId = id;

            try {
              await mainService.processLotteryResult(id);

              if (result) {
                result.isProcessing = true;
              }

              toast.success("Processamento enviado com sucesso!");
            } catch (error) {
              toast.error(handleErrorMessage(error));
            } finally {
              this.processingResultId = null;
            }
          }
        },
      });
      setTimeout(() => {
        document.getElementsByClassName("vc-btn left")[0]?.focus();
      }, 1);
    },
    showWinningTickets(lotteryResultId) {
      this.selectedLotteryResultId = lotteryResultId;
      this.showWinningTicketsModal = true;
    },
    closeWinningTicketsModal() {
      this.showWinningTicketsModal = false;
      this.selectedLotteryResultId = null;
    },
    showLotteryNumbers(lotteryResultId) {
      this.loadingNumbers = true;
      this.showLotteryNumbersModal = true;
      this.selectedLotteryNumbers = null;
      
      mainService
        .getLotteryResultsById(lotteryResultId)
        .then((response) => {
          let result = response.data;
          
          // Se os campos de lottery estão nulos, vamos buscar das informações já carregadas
          if (!result.lottery || !result.lotteryDetail) {
            // Procurar nas loterias carregadas
            for (let lottery of this.lotteries) {
              if (result.lotteryId && lottery.id === result.lotteryId) {
                if (!result.lottery) {
                  result.lottery = lottery.name;
                }
                if (!result.lotteryDetail && lottery.details) {
                  const detail = lottery.details.find(d => d.id === result.lotteryDetailId);
                  if (detail) {
                    result.lotteryDetail = detail.description;
                  }
                }
                break;
              }
            }
            
            // Se ainda estão vazios, usar valores padrão
            if (!result.lottery) {
              result.lottery = 'Loteria não informada';
            }
            if (!result.lotteryDetail) {
              result.lotteryDetail = 'Horário não informado';
            }
          }
          
          this.selectedLotteryNumbers = result;
        })
        .catch((error) => {
          toast.error(handleErrorMessage(error));
          this.closeLotteryNumbersModal();
        })
        .finally(() => {
          this.loadingNumbers = false;
        });
    },
    closeLotteryNumbersModal() {
      this.showLotteryNumbersModal = false;
      this.selectedLotteryNumbers = null;
      this.loadingNumbers = false;
    },
    clearFilters() {
      // Resetar filtros para o período padrão (último mês)
      const _fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      let dateTo = new Date();
      let dateFrom = new Date();
      dateFrom.setMonth(dateFrom.getMonth() - 1);
      this.searchDateFrom = _fmt(dateFrom);
      this.searchDateTo = _fmt(dateTo);
      this.searchLotteryId = null;
      
      // Executar nova busca
      this.searchLotteryResults(1);
    },
    formatNumberByPosition(number, position) {
      const raw = String(number ?? '').trim();
      if (!raw || raw === '-') return '—';
      const numberStr = raw.replace(/\D/g, '');
      if (!numberStr) return '—';
      if (position === 7) {
        return numberStr.slice(-3).padStart(3, '0');
      }
      return numberStr.padStart(4, '0');
    },
    async searchExternalResults() {
      if (!this.externalSearchDate || !this.externalSearchState) {
        toast.error("Por favor, selecione a data e o estado!");
        return;
      }

      this.loadingExternalResults = true;
      this.externalResultsSearched = false;
      this.externalResults = [];

      try {
        const response = await mainService.fetchExternalResults(this.externalSearchState, this.externalSearchDate);
        
        if (response.data && response.data.length > 0) {
          this.externalResults = response.data;
          this.externalResultsSearched = true;
        } else {
          this.externalResults = [];
          this.externalResultsSearched = true;
          toast.info("Nenhum resultado encontrado para a data e estado selecionados.");
        }
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.externalResults = [];
        this.externalResultsSearched = true;
      } finally {
        this.loadingExternalResults = false;
      }
    },
    clearExternalFilters() {
      const dateTo = new Date();
      this.externalSearchDate = `${dateTo.getFullYear()}-${String(dateTo.getMonth() + 1).padStart(2, '0')}-${String(dateTo.getDate()).padStart(2, '0')}`;
      this.externalSearchState = "";
      this.externalResults = [];
      this.externalResultsSearched = false;
    },
    formatExternalNumber(number, position) {
      return this.formatNumberByPosition(number, position);
    },
    async importExternalResult(externalResult) {
      if (!this.canCreateEditDelete) {
        toast.error("Você não tem permissão para importar resultados!");
        return;
      }

      // Replicar a lógica do backend LotteryNameMapper
      const externalLotteryName = (externalResult.lotteryName || "").trim();
      
      if (!externalLotteryName) {
        toast.error("Não foi possível identificar a loteria e horário do resultado.");
        return;
      }

      let lotteryName = null;
      let timeStr = null;

      // Função para extrair horário (replicando ExtractTime do backend — só a hora)
      const extractTime = (name) => {
        const match = name.match(/(\d{1,2})[hH]/);
        if (match && match[1]) {
          return match[1] + "H";
        }
        return "";
      };

      // Extrai horário preservando minutos (ex: "19h30" → "19H30")
      const extractTimeWithMinutes = (name) => {
        const match = name.match(/(\d{1,2})[hH](\d{2})?/);
        if (!match || !match[1]) return "";
        const hour = match[1];
        const minutes = match[2] || "";
        if (!minutes || minutes === "00") return hour + "H";
        return `${hour}H${minutes}`;
      };

      // Função para normalizar horário (replicando NormalizeTime do backend)
      const normalizeTime = (time) => {
        if (!time) return "";
        time = time.trim().toUpperCase();
        // Formato com minutos (ex: "19H30") já está normalizado
        if (/^\d{1,2}H\d{2}$/.test(time)) return time;
        if (!time.endsWith("H")) {
          time += "H";
        }
        return time;
      };

      // Converte "XH" ou "XHMM" para "HH:MM" (ex: "8H" → "08:00", "19H30" → "19:30")
      const convertToTimeFormat = (timeInHourFormat) => {
        if (!timeInHourFormat) return "";

        const normalized = timeInHourFormat.trim().toUpperCase();

        // Formato com minutos: "19H30" → "19:30"
        const withMinutes = normalized.match(/^(\d{1,2})H(\d{2})$/);
        if (withMinutes) {
          const hour = parseInt(withMinutes[1], 10);
          const minute = parseInt(withMinutes[2], 10);
          if (!isNaN(hour) && !isNaN(minute) && hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59) {
            return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
          }
        }
        
        // Remover "H" e espaços
        const hourStr = normalized.replace("H", "").trim();
        
        // Tentar converter para número
        const hour = parseInt(hourStr, 10);
        if (!isNaN(hour) && hour >= 0 && hour <= 23) {
          // Formatar como "HH:00"
          return `${hour.toString().padStart(2, '0')}:00`;
        }
        
        return "";
      };

      // Aplicar mapeamentos especiais (mesma ordem do backend)
      const nameUpper = externalLotteryName.toUpperCase();
      
      if (nameUpper.startsWith("BANDEIRANTES")) {
        lotteryName = "PT-SP";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("CORUJA-RIO")) {
        lotteryName = "PT-RIO";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("PTN-SP")) {
        lotteryName = "PT-SP";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("PTM-RIO")) {
        lotteryName = "PT-RIO";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("PTV-RIO")) {
        lotteryName = "PT-RIO";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("PTN-RIO")) {
        // PTN-RIO 18h20 = PT-RIO 18:20 (mantido para quando voltar no Ponto do Bicho)
        lotteryName = "PT-RIO";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("PT-RIO")) {
        // PT-RIO no resultadoonline.net (hora cheia, ex.: 18h → 18:00)
        lotteryName = "PT-RIO";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else if (nameUpper.startsWith("LSORTE") || nameUpper.startsWith("LS-RS")) {
        // LSORTE no resultadoonline.net é a LS-RS do sistema
        lotteryName = "LS-RS";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else if (nameUpper.startsWith("PT-SUL")) {
        lotteryName = "PT-SUL";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("RS")) {
        lotteryName = "LS-RS";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("FEDERAL")) {
        // FEDERAL 11h / 20h = resultadoonline.net; valida contra o banco
        lotteryName = "FEDERAL";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else if (nameUpper.startsWith("LBR")) {
        // LBR 08h30 = LBR 08:30 (Resultado Fácil; preserva minutos)
        lotteryName = "LBR";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else if (nameUpper.startsWith("PARATODOS")) {
        lotteryName = "PARATODOS";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else if (nameUpper.startsWith("LOTEP")) {
        lotteryName = "LOTEP";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else if (nameUpper.startsWith("LOOK")) {
        lotteryName = "LOOK";
        timeStr = extractTime(externalLotteryName);
      } else if (nameUpper.startsWith("CAPITAL")) {
        lotteryName = "CAPITAL";
        timeStr = extractTimeWithMinutes(externalLotteryName);
      } else {
        // Para outros casos, extrair o nome da loteria e o horário
        const parts = externalLotteryName.split(/\s+/).filter(p => p);
        
        if (parts.length >= 2) {
          lotteryName = parts[0];
          timeStr = extractTime(externalLotteryName);
        } else if (parts.length === 1) {
          // Tentar extrair horário do próprio nome (ex: "PT-SP8h")
          timeStr = extractTime(externalLotteryName);
          lotteryName = externalLotteryName.replace(/(\d{1,2})[hH]/i, "").trim();
        }
      }

      // Normalizar o horário
      timeStr = normalizeTime(timeStr);
      // Converter para formato "HH:MM" para comparar com o banco de dados
      const timeInFormat = convertToTimeFormat(timeStr);

      if (!lotteryName || !timeStr || !timeInFormat) {
        toast.error("Não foi possível identificar a loteria e horário do resultado.");
        return;
      }

      // Encontrar a loteria no sistema
      const lottery = this.lotteries.find(l => 
        l.name.toUpperCase() === lotteryName.toUpperCase()
      );

      if (!lottery) {
        toast.error(`Loteria "${lotteryName}" não encontrada no sistema.`);
        return;
      }

      // Encontrar o horário (detail) correspondente
      // O banco armazena no formato "HH:MM" (ex: "08:00", "16:00", "21:00", "15:45", "19:30")
      let detail = lottery.details?.find(d => (d.description || "").trim() === timeInFormat);

      // PT-RIO: API/scraper usam só a hora em alguns casos; no cadastro os minutos importam.
      // 18:20 = PTN-RIO | 19:30 = Resultado Fácil
      if (!detail && lotteryName.toUpperCase() === "PT-RIO") {
        if (timeInFormat === "18:00") {
          detail = lottery.details?.find(d => (d.description || "").trim() === "18:20");
        } else if (timeInFormat === "19:00") {
          detail = lottery.details?.find(d => (d.description || "").trim() === "19:30");
        }
      }

      if (!detail) {
        detail = lottery.details?.find(d => {
          const description = (d.description || "").trim();
          // Fallback: comparar apenas a hora (ex: "15:45" vs "15:00")
          if (description.includes(":")) {
            const descHour = description.split(":")[0].padStart(2, '0');
            const timeHour = timeInFormat.split(":")[0].padStart(2, '0');
            return descHour === timeHour;
          }
          return false;
        });
      }

      if (!detail) {
        toast.error(`Horário "${timeStr}" não encontrado para a loteria "${lotteryName}". Horários disponíveis: ${lottery.details?.map(d => d.description).join(", ") || "nenhum"}`);
        return;
      }

      // Confirmar importação
      this.$confirm({
        message: `Deseja importar o resultado de ${lotteryName} - ${detail.description} para o sistema?`,
        button: { no: "Não", yes: "Sim" },
        callback: async (confirm) => {
          if (confirm) {
            this.importingResultId = externalResult.lotteryName;
            
            try {
              await mainService.importExternalResult(
                this.externalSearchState,
                this.externalSearchDate,
                detail.id,
                externalResult.numbers.map((number, index) =>
                  this.formatNumberByPosition(number, index + 1)
                )
              );

              toast.success("Resultado importado com sucesso!");
              
              // Recarregar a lista de resultados
              this.searchLotteryResults(1);
              
              // Remover o resultado importado da lista externa
              this.externalResults = this.externalResults.filter(r => r.lotteryName !== externalResult.lotteryName);
            } catch (error) {
              const errorMessage = handleErrorMessage(error);
              // Verificar se é erro de duplicado
              if (errorMessage.toLowerCase().includes("já existe") || 
                  errorMessage.toLowerCase().includes("duplicado") ||
                  errorMessage.toLowerCase().includes("already exists")) {
                toast.warning(`Resultado já existe para ${lotteryName} - ${detail.description} na data ${this.externalSearchDate}`);
              } else {
                toast.error(errorMessage);
              }
            } finally {
              this.importingResultId = null;
            }
          }
        },
      });
    }
  }
};
</script>

<style scoped>
/* Mensagem de permissão */
.permission-info {
  padding: 7px;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  margin-bottom: 20px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Aba desabilitada */
.tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}

.tab-button.disabled:hover {
  background-color: var(--bg-secondary) !important;
  color: var(--text-muted) !important;
}

/* Indicador de apenas visualização */
.no-actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.875rem;
  font-style: italic;
}

.no-actions i {
  color: var(--accent-primary);
}

/* Botão de bilhetes premiados */
.btn-prizes {
  background-color: #f39c12;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 0.25rem;
}

.btn-prizes:hover {
  background-color: #e67e22;
  transform: translateY(-1px);
}

.btn-prizes i {
  font-size: 0.875rem;
}

.btn-process {
  background-color: #0ea5e9;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 0.25rem;
}

.btn-process:hover:not(:disabled) {
  background-color: #0284c7;
  transform: translateY(-1px);
}

.btn-process:disabled {
  opacity: 0.6;
  cursor: wait;
}

.btn-process i {
  font-size: 0.875rem;
}

/* Coluna de números premiados */
.winning-numbers-cell {
  display: flex;
  justify-content: center;
  align-items: center;
}

.winning-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.badge-success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.badge-danger {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.badge-warning {
  background-color: #fff3cd;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.badge-muted {
  background-color: #f8f9fa;
  color: #6c757d;
  border: 1px solid #dee2e6;
}

.winning-badge i {
  font-size: 0.7rem;
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.status-badge i {
  font-size: 0.7rem;
}

/* Botão de números sorteados */
.btn-numbers {
  background-color: #6c5ce7;
  color: white;
  border: none;
  padding: 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  margin-right: 0.25rem;
}

.btn-numbers:hover {
  background-color: #5a4fcf;
  transform: translateY(-1px);
}

.btn-numbers i {
  font-size: 0.875rem;
}

/* Modal de números sorteados */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(3px);
}

.modal-content {
  background: var(--bg-primary);
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.numbers-modal {
  max-width: 700px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-title i {
  color: var(--accent-primary);
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close:hover {
  background-color: var(--bg-secondary);
  color: var(--text-primary);
}

.modal-loading {
  padding: 3rem;
  text-align: center;
  color: var(--text-muted);
}

.modal-loading .loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.modal-loading i {
  font-size: 2rem;
  color: var(--accent-primary);
}

.modal-body {
  padding: 1.5rem;
}

.lottery-info {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--border-color);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.info-item i {
  color: var(--accent-primary);
  width: 16px;
  text-align: center;
}

.info-label {
  font-weight: 600;
  color: var(--text-primary);
  min-width: 80px;
}

.info-value {
  color: var(--text-secondary);
}

.numbers-section .section-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.numbers-section .section-title i {
  color: var(--accent-primary);
}

.numbers-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.number-card {
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  border-radius: 12px;
  padding: 1rem;
  text-align: center;
  color: white;
  box-shadow: 0 4px 12px rgba(52, 120, 242, 0.2);
  transition: transform 0.2s;
}

.number-card:hover {
  transform: translateY(-2px);
}

.number-position {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  opacity: 0.9;
  margin-bottom: 0.25rem;
}

.number-value {
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.1em;
}

.modal-footer {
  padding: 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

/* Filtros melhorados */
.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.filters-actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.filters-actions .btn-secondary {
  background-color: var(--bg-secondary);
  color: var(--text-secondary);
  border: 1px solid var(--border-color);
}

.filters-actions .btn-secondary:hover {
  background-color: var(--text-muted);
  color: white;
}

/* Seção de Resultados Externos */
.external-results-section {
  margin-top: 2rem;
}

.external-results-card {
  background: var(--card-background, #ffffff);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid var(--border-color, #e2e8f0);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.external-results-card .card-header {
  margin-bottom: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
}

.external-results-card .card-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 0;
  color: var(--text-primary);
  font-size: 1.25rem;
  font-weight: 600;
}

.external-results-card .card-title i {
  color: var(--accent-primary, #3478f2);
}

.external-results-card .card-subtitle {
  color: var(--text-secondary);
  font-size: 0.9rem;
  margin: 0;
  text-align: right;
}

.external-filters {
  margin-bottom: 2rem;
}

.external-filters-inline {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: flex-end;
  flex-wrap: wrap;
}

.external-filters-inline .input-group {
  flex: 1;
  min-width: 200px;
}

.external-filters-inline .input-group:last-child {
  flex: 0 0 auto;
  display: flex;
  align-items: flex-end;
}

.btn-search-inline {
  min-width: 180px;
  height: fit-content;
  padding: 0.75rem 1.5rem;
  margin-top: 0;
}

.external-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-muted);
  font-size: 0.85rem;
  padding: 0.5rem;
  background: var(--bg-secondary, #f8f9fa);
  border-radius: 6px;
}

.external-info i {
  color: var(--accent-primary, #3478f2);
}

.external-info.rs-info {
  background: linear-gradient(135deg, #fef3c7, #fde68a);
  border: 1px solid #f59e0b;
  color: #92400e;
  font-size: 0.9rem;
}

.external-info.rs-info i {
  color: #f59e0b;
}

.external-loading {
  padding: 2rem;
  text-align: center;
  color: var(--text-muted);
}

.external-results-list {
  margin-top: 1.5rem;
}

.external-results-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.external-results-title i {
  color: var(--accent-primary, #3478f2);
}

.external-results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
}

.external-result-card {
  background: var(--bg-secondary, #f8f9fa);
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid var(--border-color, #e2e8f0);
  transition: transform 0.2s, box-shadow 0.2s;
}

.external-result-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.external-result-header {
  margin-bottom: 1rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid var(--border-color, #e2e8f0);
}

.external-result-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  color: var(--text-primary);
  font-size: 1rem;
  font-weight: 600;
}

.external-result-title i {
  color: var(--accent-primary, #3478f2);
}

.external-result-numbers {
  margin-bottom: 1rem;
}

.external-numbers-table {
  width: 100%;
  border-collapse: collapse;
}

.external-numbers-table thead {
  background: var(--bg-primary, #ffffff);
}

.external-numbers-table th {
  padding: 0.75rem;
  text-align: center;
  font-weight: 600;
  font-size: 0.85rem;
  color: var(--text-primary);
  border-bottom: 2px solid var(--border-color, #e2e8f0);
}

.external-numbers-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
  text-align: center;
}

.external-numbers-table tbody tr:last-child td {
  border-bottom: none;
}

.prize-badge {
  display: inline-block;
  background: linear-gradient(135deg, var(--accent-primary, #3478f2), var(--accent-secondary, #5a9));
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-align: center;
}

.number-display {
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: 0.1em;
  text-align: center;
  display: inline-block;
  width: 100%;
}

.external-result-actions {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border-color, #e2e8f0);
}

.btn-import {
  width: 100%;
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: none;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-import:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669, #047857);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.3);
}

.btn-import:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.external-empty-state {
  padding: 3rem;
  text-align: center;
  color: var(--text-muted);
}

.external-empty-state i {
  font-size: 3rem;
  margin-bottom: 1rem;
  color: var(--text-muted);
  opacity: 0.5;
}

.external-empty-state p {
  margin: 0;
  font-size: 1rem;
}

/* Paginação (mesmo esquema do ViewSellerBets) */
.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  margin-left: 0.5rem;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition, all 0.2s);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border, var(--border-color));
  background: var(--bg-primary);
}

/* Grupo e Animal na tabela externa */
.group-badge {
  display: inline-block;
  background: linear-gradient(135deg, #f39c12, #e67e22);
  color: white;
  padding: 0.2rem 0.5rem;
  border-radius: 6px;
  font-size: 0.8rem;
  font-weight: 700;
  min-width: 28px;
  text-align: center;
}

.animal-text {
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-style: italic;
}

.text-muted-small {
  font-size: 0.8rem;
  color: var(--text-muted);
  opacity: 0.6;
}

/* Grupo e Animal no card do modal de números */
.number-meta {
  margin-top: 0.4rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
}

.number-group {
  font-size: 0.7rem;
  font-weight: 700;
  background: rgba(255,255,255,0.25);
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
  letter-spacing: 0.05em;
}

.number-animal {
  font-size: 0.7rem;
  opacity: 0.9;
  font-style: italic;
}

/* Tabela de Números Sorteados (formulário) - alinhamento cabeçalhos e valores */
.numbers-table-container {
  width: 100%;
  overflow-x: auto;
}

.numbers-table {
  width: 100%;
  border-collapse: collapse;
}

.numbers-table thead th {
  padding: 0.75rem 0.5rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-primary);
  border-bottom: 2px solid var(--border-color, #e2e8f0);
  vertical-align: middle;
}

.numbers-table tbody td {
  padding: 0.5rem;
  text-align: left;
  vertical-align: middle;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}

.numbers-table .position-cell {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  text-align: left;
}

.numbers-table .number-input,
.numbers-table .group-input,
.numbers-table .animal-input {
  display: block;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  text-align: left;
}

/* Inputs de grupo e animal no formulário */
.group-input {
  width: 70px !important;
  max-width: 100%;
}

.animal-input {
  width: 140px !important;
  max-width: 100%;
}

/* Dica do formulário */
.form-hint {
  margin-top: 0.75rem;
  font-size: 0.82rem;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 0.4rem;
}

.form-hint i {
  color: var(--accent-primary);
}

/* Responsividade */
@media (max-width: 768px) {
  .external-results-grid {
    grid-template-columns: 1fr;
  }

  .external-filters-grid {
    grid-template-columns: 1fr;
  }

  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }
}


</style>

